import clsx from 'clsx';
import type React from 'react';

/**
 * Navigation bar in the header.
 *
 * <HeaderBar>
 *   <HeaderBarMain>
 *     <HeaderBarBreadcrumbs>
 *       <Breadcrumbs />
 *     </HeaderBarBreadcrumbs>
 *     <HeaderBarTabs>
 *       <ButtonTabs>
 *         <ButtonTab label="View" />
 *         <ButtonTab label="Edit" />
 *       </ButtonTabs>
 *     </HeaderBarTabs>
 *   </HeaderBarMain>
 *   <HeaderBarActions>
 *     <HeaderBarActionsSide>
 *       <Button label="Integrations" kind="blank" iconOnly />
 *       <Button label="Share" kind="blank" iconOnly />
 *     </HeaderBarActionsSide>
 *     <HeaderBarActionsMain>
 *      <Button label="Visit" kind="primary" />
 *     </HeaderBarActionsMain>
 *   </HeaderBarActions>
 * </HeaderBar>
 */
export function HeaderBar(props: React.PropsWithChildren<React.HTMLAttributes<HTMLElement>>) {
    const { children, ...rest } = props;
    return (
        <header
            className="flex flex-col gap-x-3 border-b xl:flex-row xl:items-center xl:px-2.5"
            {...rest}
        >
            {children}
        </header>
    );
}

/**
 * Main section of the header bar, containing breadcrumbs and tabs.
 */
export function HeaderBarMain(props: React.PropsWithChildren) {
    return (
        <div className="flex h-[calc(var(--app-toolbar-height)-1px)] items-center gap-2 px-5 xl:flex-1 xl:p-0">
            {props.children}
        </div>
    );
}

/**
 * Breadcrumbs in the header bar.
 */
export function HeaderBarBreadcrumbs(props: React.PropsWithChildren) {
    return <div className="flex items-center gap-1">{props.children}</div>;
}

/**
 * Tabs in the header bar.
 */
export function HeaderBarTabs(props: React.PropsWithChildren) {
    return (
        <div className="flex flex-1 gap-1 overflow-auto xl:min-w-min xl:shrink-0 xl:overflow-visible">
            {props.children}
        </div>
    );
}

/**
 * Actions in the header bar.
 */
export function HeaderBarActions(props: React.PropsWithChildren) {
    return (
        <div
            className={clsx(
                'flex h-[calc(var(--app-toolbar-height)-1px)] items-center border-t px-5',
                'xl:min-w-min xl:shrink-0 xl:border-t-0 xl:px-0'
            )}
        >
            {props.children}
        </div>
    );
}

/**
 * Side actions in the header bar.
 * Intended for secondary actions like "Integrations" or "Member access".
 */
export function HeaderBarActionsSide(props: React.PropsWithChildren) {
    return (
        <div className="flex items-center gap-1 overflow-auto xl:overflow-visible">
            {props.children}
        </div>
    );
}

/**
 * Separator used to split actions into smaller groups.
 */
export function HeaderActionsSeparator() {
    return <div className="hidden h-6 border-r sm:flex" />;
}

/**
 * Main actions in the header bar.
 * Intended for primary actions like "Visit" or "Edit".
 */
export function HeaderBarActionsMain(props: React.PropsWithChildren) {
    return (
        <div className="flex shrink-0 flex-grow items-center justify-end gap-1.5 px-2 first:pl-0 last:pr-0 xl:flex-grow-0">
            {props.children}
        </div>
    );
}
